// Profile API types + wrapper (W36.1, per `docs/spec/profile/README.md` §5.1).
//
// Mirrors `lib/api/anniversary.ts`: hand-written types against
// `backend/docsnew/11-api/openapi.yaml`, `openapi-fetch` for transport, and
// `ApiError` so callers switch on a stable machine code rather than a message.
//
// The avatar field is `avatar_url` and only `avatar_url`. Profile spec §8 B2
// records that the DB column (`avatar_path`), the serialised key and the
// TypeScript type had three different stories; the API settled on one name and
// this file is where that choice is expressed on the client.

import { api as openapi } from "@/lib/api/client";
import { ApiError } from "@/lib/api/pair";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyClient = Record<"GET" | "POST" | "PUT" | "DELETE" | "PATCH", any>;
const api = openapi as unknown as AnyClient;

/** Which side of the segmented control a profile belongs to. */
export type ProfileRelation = "self" | "partner";

export interface ProfileRead {
  user_id: string;
  display_name: string;
  /** Present on both tabs — profile spec §2.5 P9 has no visibility switch. */
  email: string | null;
  bio: string | null;
  avatar_media_id: string | null;
  /**
   * The one avatar name. `null` means "no avatar", and the UI draws the
   * `display_name` initial card. A non-null value that 404s falls back to the
   * same card (B4), so this never has to be treated as trustworthy.
   */
  avatar_url: string | null;
  birthday: string | null;
  /** Feb 29 substitute; both null means "no choice on record" (ADR-0050 §5). */
  birthday_non_leap_month: number | null;
  birthday_non_leap_day: number | null;
  /** Derived server-side, never stored (profile spec §4.2). */
  zodiac: string | null;
  /** "今年 N 歲". */
  age_turning: number | null;
  relation: ProfileRelation;
  /**
   * Account creation time — the first row of the birthday card's by-the-year
   * table (profile spec §2.2 P10). Without it the table would either start at
   * the birth year or guess, and a 30-year-old who joined last year would see
   * thirty rows instead of one.
   */
  created_at: string;
  birthday_anniversary_id: string | null;
  birthday_reminder_days: number[];
}

export interface ProfileUpdateInput {
  display_name?: string;
  /** `""` clears the bio. */
  bio?: string;
  /** ISO date. Omit to leave unchanged. */
  birthday?: string;
  birthday_non_leap_month?: number;
  birthday_non_leap_day?: number;
}

export interface AvatarRead {
  media_id: string | null;
  avatar_url: string | null;
}

interface CallResult {
  response: Response;
  data: unknown;
}

async function call(
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH",
  path: string,
  opts: Record<string, unknown> = {},
): Promise<CallResult> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result = await (api as any)[method](path, opts);
  return { response: result.response, data: (result.data as unknown) ?? null };
}

async function toApiError(res: CallResult, fallbackCode: string): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await res.response.clone().json();
  } catch {
    body = null;
  }
  const envelope = (body as { error?: { code?: string; message?: string } } | null)?.error;
  // ApiError takes (status, code, message) — status first, per
  // lib/api/pair.ts:116.
  return new ApiError(
    res.response.status,
    envelope?.code ?? fallbackCode,
    envelope?.message ?? "請求失敗",
  );
}

async function request<T>(
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH",
  path: string,
  opts: Record<string, unknown> = {},
  fallbackCode = "PROFILE_REQUEST_FAILED",
): Promise<T> {
  const res = await call(method, path, opts);
  if (!res.response.ok) throw await toApiError(res, fallbackCode);
  return res.data as T;
}

export function getMyProfile(signal?: AbortSignal): Promise<ProfileRead> {
  return request<ProfileRead>("GET", "/api/v1/profile/me", signal ? { signal } : {});
}

export function getPartnerProfile(memberUserId: string): Promise<ProfileRead> {
  return request<ProfileRead>("GET", `/api/v1/profile/${memberUserId}`);
}

export function updateMyProfile(input: ProfileUpdateInput): Promise<ProfileRead> {
  return request<ProfileRead>("PATCH", "/api/v1/profile/me", { body: input });
}

export function uploadAvatar(file: File): Promise<AvatarRead> {
  const form = new FormData();
  form.append("file", file);
  // The body is FormData: openapi-fetch must not set a JSON content-type or
  // the boundary will be missing and the multipart parse fails server-side.
  return request<AvatarRead>("POST", "/api/v1/profile/me/avatar", { body: form });
}

export function deleteAvatar(): Promise<AvatarRead> {
  return request<AvatarRead>("DELETE", "/api/v1/profile/me/avatar");
}

/**
 * The `src` for an `AuthorAvatar`, or `undefined` so the primitive draws the
 * initial card.
 *
 * Profile spec §8 B3 asks for a resolver on the client because the backend can
 * now hand back two shapes: a served path from the media module, and — for
 * rows written before W36.1 — a raw string in the legacy `avatar_path` column
 * that may be a bare filename, a full URL, or garbage. Normalising that here
 * means every call site stays a one-liner and none of them have to know which
 * era a row came from.
 */
export function avatarUrl(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (/^(https?:)?\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("/")) return trimmed;
  // A bare legacy value has no route to be served from; returning it would
  // produce a same-origin request for a path that does not exist. Undefined
  // makes the avatar fall back to the initial card, which is the honest state.
  if (trimmed.includes("/") || trimmed.includes(".")) return `/${trimmed.replace(/^\/+/, "")}`;
  return undefined;
}

/** The initial-character card fallback, matching `AuthorAvatar`'s own rule. */
export function avatarInitial(displayName: string | null | undefined, tone: "you" | "partner"): string {
  const first = (displayName ?? "").trim().slice(0, 1);
  return first || (tone === "you" ? "你" : "對");
}
